import Modal from "@src/components/UIElements/Modal";
import styles from "../../../Tabs/Content/styles/NotesIcon.module.scss";
import Image from "@src/lib/Image";
import { getAwsAssets, hitHapticFeedback } from "@src/utils";
import { useDispatch, useSelector } from "react-redux";
import { showNotesModalAction } from "../../../Tabs/Content/store/actions";
import { CONTENT_TYPE } from "../../../Tabs/Content/store/constants";
import ContentDownloader from "../../../Tabs/Content/components/ContentList/ContentDownloader";
import ContentShimmmer from "../../../Tabs/Content/components/ContentShimmer";
import ListItemDefault from "../../../Tabs/Content/components/ContentList/ListItem";
import { funnelTrackAction } from "../../../Tabs/Overview/store/actions";
import { installmentsAlertStateEnum, trackIDMap } from "../../../Tabs/Overview/store/constants";
import { useRouter } from "@src/lib/router";
import handleOpenContent from "../../../Tabs/Content/helpers/handleOpenContent";
import { showContentLockedBottomSheetAction, showCourseLockedBottomSheetAction, showViewsExhaustedBottomSheetAction } from "../../store/actions";

const NotesAttachmentModal = (props) => {

  const {
    content,
    getOfflineDownloadedItemProgress,
    getOfflineDownloadedItemState,
    handleGetOfflineDownloadList,
    handleSetCheckProgressInterval,
    handleClearCheckProgressInterval,
  } = props;

  const router = useRouter();
  const dispatch = useDispatch();
  const renderImage = (format, url) => {
    if (format === "png" || format === "jpeg") {
      return (
        <img
          src={url}
          alt="downArrowIcon"
          height={60}
          width={80}
        />
      );
    } else if (format === "test") {
      return (
        <div className={styles.pdfThumbnail}>
          <Image
            src={getAwsAssets("onlineTestThumbnail.svg", "content")}
            alt="onlineTestIcon"
            height={60}
            width={80}
            eager={true}
            objectFit="contain"
          />
        </div>
      );
    } else {
      return (
        <div className={styles.pdfThumbnail}>
          <Image
            src={getAwsAssets("pdfThumbnail.svg", "content")}
            alt="downArrowIcon"
            height={60}
            width={80}
            eager={true}
            objectFit="contain"
          />
        </div>
      );
    }
  };

  const handleCloseModal = () => {
    dispatch(showNotesModalAction(false));
  };

  const state = useSelector(
    (state) => state?.overviewReducer?.overViewDetails?.course
  );

  const isCourseLocked = useSelector(
    (state) =>
      state?.overviewReducer?.overViewDetails?.course?.installments
        ?.installmentAlertState
  );

  const loadingNotesContent = useSelector(
    (state) => state?.courseContentReducer?.loadingNotesContent
  );

  const handleViewContentItem = (item) => {
    const isTest = item?.mediaType === "test";
    // The video/attachments API returns a payload shape that differs from
    // a regular content item: `contentId` holds the parent content id
    // (while `id` is the attachment's own id) and the test type is exposed
    // as `type` instead of `typeOfTest`. `handleOpenContent` (and the
    // downstream tb-access call for typeOfTest === 2 / realtime tests)
    // reads `id` and `typeOfTest`, so we remap them here before handing
    // the payload off — otherwise the typeOfTest === 2 branch is skipped
    // and the wrong contentId is sent to tb-access.
    const itemPayload = isTest
      ? {
          ...item,
          id: item?.contentId,
          typeOfTest: item?.type,
          URL: item?.url,
          contentType: CONTENT_TYPE.TEST,
        }
      : { ...item, contentType: CONTENT_TYPE.DOCUMENT, format: item?.mediaType };
    handleOpenContent({
      ...itemPayload,
      courseId: +router?.query?.courseId,
      originalCourseId: parseInt(router?.query?.originalCourseId),
    });
  };

  const handleItemClicked = (item) => {
    const isTest = item?.mediaType === "test";
    localStorage.setItem("currentItem", JSON.stringify(isTest ? item : content));
    hitHapticFeedback();
    dispatch(
      funnelTrackAction({
        type: trackIDMap.contentView,
        isPurchased:
          state?.installments?.paidInstallmentSummary?.isPaymentComplete === 1 || 
          state?.metaData?.isPurchased === 1,
        courseId: router?.query?.courseId,
        folderId: router?.query?.folderId || 0,
        contentId: content?.id,
        contentType: isTest ? CONTENT_TYPE.TEST : CONTENT_TYPE.DOCUMENT,
        format: item?.mediaType
      })
    );
    if (isCourseLocked === installmentsAlertStateEnum.COURSE_LOCKED && content?.isLocked) {
      dispatch(showCourseLockedBottomSheetAction(true));
      return;
    }
    if (
      content.isScheduled ||
      (!content.isPurchased && content?.isLocked)
    ) {
      dispatch(showContentLockedBottomSheetAction(true));
    } else handleViewContentItem(item);
  }

  return (
    <Modal
      handleClose={handleCloseModal}
      header={content?.name}
    >
      <div>
        <ListItemDefault
          key={content.id}
          content={content}
          getOfflineDownloadedItemProgress={getOfflineDownloadedItemProgress}
          getOfflineDownloadedItemState={getOfflineDownloadedItemState}
          handleGetOfflineDownloadList={handleGetOfflineDownloadList}
          handleSetCheckProgressInterval={handleSetCheckProgressInterval}
          handleClearCheckProgressInterval={handleClearCheckProgressInterval}
        />
        {loadingNotesContent && !content?.attachments?.length && (
          <ContentShimmmer />
        )}
        {content?.attachments?.map((item, i) => {
          // Live class recordings ship their attachments inline with the
          // initial content payload, so the modal never has to fire
          // `getContentAttachmentsAction` for them. Non-live videos instead
          // expose only a `hasAttachments: 1` flag and rely on that extra
          // fetch to populate `attachments`. We use that asymmetry to
          // identify live recordings here and restore the legacy permissive
          // flow: PDF attachments on live recordings show the download
          // button regardless of `canDownload`. Every other source continues
          // to gate on `canDownload === 1`.
          const isLiveRecordingAttachment = !content?.hasAttachments;
          const showDownloadForPdf =
            item?.mediaType === "pdf" &&
            (isLiveRecordingAttachment || item?.canDownload === 1);

          return (
            <div key={i} className={styles.notesCardWrapper} onClick={()=>handleItemClicked(item)}>
              <div>
                {renderImage(item?.mediaType, item?.url)}
              </div>
              <p className={styles.name}>{item?.name}</p>
              {showDownloadForPdf && <ContentDownloader
                content={{
                  ...item, 
                  contentType: 3,
                  format: item.mediaType
                }}
                handleGetOfflineDownloadList={handleGetOfflineDownloadList}
                getOfflineDownloadedItemProgress={
                  getOfflineDownloadedItemProgress
                }
                getOfflineDownloadedItemState={getOfflineDownloadedItemState}
                handleSetCheckProgressInterval={handleSetCheckProgressInterval}
                handleClearCheckProgressInterval={handleClearCheckProgressInterval}
              />}
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

export default NotesAttachmentModal;
